import React, { forwardRef, useState } from 'react'
import { DialogTitle, DialogContent, Dialog, Box } from '@mui/material'
import styles from './TeenagerContractSignModal.module.scss'
import { useTranslation } from 'react-i18next'
import CloseButton from '@/components/CloseButton/CloseButton'
import { useDispatch } from 'react-redux'
import { Field as FormikField, Form, Formik } from 'formik'
import { contractsThunks } from '@/entity'
import Input from '@mui/material/Input'
import InputMask from 'react-input-mask'
import * as yup from 'yup'
import cn from 'classnames'
import { InputOTP } from '@/components/InputOTP/InputOTP'
import { message } from 'antd'
import { DigitalSign } from '@/utils/digitalSign'
import { Button } from '@/ui'
import { blockScreen, unblockScreen } from '@/shared'

const FORM_NAME = {
	confirmCode: 'confirmCode',
	mobilePhone: 'mobilePhone',
}

const formInitialState = {
	[FORM_NAME.confirmCode]: '',
	[FORM_NAME.mobilePhone]: '',
}

const TYPES = {
	CONFIRM: 'CONFIRM',
	OTP: 'OTP',
}

const TextMaskCustom = forwardRef(function TextMaskCustom(props, ref) {
	const { ...other } = props
	return (
		<InputMask
			{...other}
			mask="+7 (999) 999-99-99"
			definitions={{
				'#': /[0-9]/,
			}}
			inputRef={ref}
		/>
	)
})

const modalInitialState = {
	item: null,
	type: {
		[TYPES.CONFIRM]: true,
		[TYPES.OTP]: false,
	},
}

export const TeenagerContractSignModal = ({
	contractType,
	selectedContract,
	role = 'employee',
	onUpdate,
	onClose,
	isOpen,
}) => {
	const { t } = useTranslation()
	const dispatch = useDispatch()
	const digitalSign = new DigitalSign()

	const contractMap = {
		eContractTerm: selectedContract?.termExternalId,
		eAdContract: selectedContract?.externalId,
		eContract: selectedContract?.externalId,
	}

	const [modalData, setModalData] = useState(modalInitialState)

	const validationSchema = yup.object({
		mobilePhone: yup.string().required(t('validation.required', { field: t('form.mobilePhone') })),
	})

	const makeSignData = () => {
		switch (contractType) {
			case 'eContract':
				return { externalId: contractMap[contractType] }
			case 'eAdContract':
				return { externalId: contractMap[contractType] }
			case 'eContractTerm':
			default:
				return { termExternalId: contractMap[contractType] }
		}
	}

	const handleAcceptToSendSms = (values) => {
		let body = {
			data: {
				...modalData.item,
				externalId: contractMap[contractType],
				[FORM_NAME.mobilePhone]: values.mobilePhone,
			},
			onSuccess: () => {
				setModalData((prevState) => ({
					...prevState,
					type: {
						[TYPES.CONFIRM]: false,
						[TYPES.OTP]: true,
					},
				}))
			},
			contractType,
		}

		dispatch(contractsThunks.sendSmsConsent(body))
	}

	const handleConfirmSms = (data) => {
		let body = {
			data: {
				...modalData.item,
				externalId: contractMap[contractType],
				[FORM_NAME.confirmCode]: data.confirmCode,
			},
			onSuccess: () => {
				handleSign()
			},
			contractType,
		}

		dispatch(contractsThunks.verifySmsConsent(body))
	}

	const handleCloseApproveSms = () => {
		setModalData(modalInitialState)
	}

	const signArray = async (xmlArray) => {
		try {
			// Блокируем экран на время подписания массива
			blockScreen()

			// Используем Promise.all для одновременной обработки всех элементов
			const results = await Promise.all(xmlArray.map((xml) => digitalSign.handleSend(xml)))

			// Разблокируем экран после завершения подписания
			unblockScreen()

			// Возвращаем результаты
			return results
		} catch (error) {
			// Разблокируем экран при возникновении ошибки
			unblockScreen()

			// Пробрасываем ошибку дальше, если нужно
			throw error
		}
	}

	const handleSign = () => {
		let body = {
			data: makeSignData(),
			payload: { role: role },
			onSuccess: async (xml) => {
				const res = contractType === 'eContractTerm' ? await digitalSign.handleSend(xml.xmlData) : await signArray(xml)

				let signedBody = {
					data: res,
					payload: { role: role },
					onSuccess: () => {
						message.success(t('successfully_subscribed'))
						setModalData((prevState) => ({
							...prevState,
							type: {
								[TYPES.CONFIRM]: false,
								[TYPES.OTP]: false,
							},
						}))
						onUpdate()
					},
					contractType,
				}
				dispatch(contractsThunks.saveSign(signedBody))
			},
			contractType,
		}
		dispatch(contractsThunks.getXml(body))
	}

	return (
		<>
			{modalData.type[TYPES.CONFIRM] && (
				<Dialog classes={{ paper: styles.sign }} onClose={onClose} open={isOpen}>
					<Formik
						initialValues={formInitialState}
						validationSchema={validationSchema}
						onSubmit={(values) => handleAcceptToSendSms(values)}
					>
						{() => {
							return (
								<Form>
									<Box>{onClose ? <CloseButton onClose={onClose}></CloseButton> : null}</Box>
									<DialogTitle classes={{ root: styles.sign__title }}>{t('approve_title')}</DialogTitle>
									<DialogContent sx={{ paddingBottom: '40px' }}>
										<div className={styles.fieldContainer}>
											<FormikField name={FORM_NAME.mobilePhone}>
												{({ field, meta: { touched, error } }) => (
													<div className={styles.fieldWrapper}>
														<Input
															classes={{ root: styles.phone__field }}
															fullWidth
															{...field}
															value={field.value || ''}
															inputComponent={TextMaskCustom}
															placeholder={t('contract.placeholder_phone')}
														/>
														<div className={styles.error}>{touched && t(error?.key)}</div>
													</div>
												)}
											</FormikField>
										</div>
										<div className={styles.buttonSmsWrapper}>
											<Button type="submit">{t('send_sms_button')}</Button>
											<Button type="bare" onClick={onClose}>
												{t('cancel')}
											</Button>
										</div>
									</DialogContent>
								</Form>
							)
						}}
					</Formik>
				</Dialog>
			)}

			{modalData.type[TYPES.OTP] && (
				<Dialog classes={{ paper: styles.sign }} onClose={onClose} open={isOpen}>
					<Box>{onClose ? <CloseButton onClose={onClose}></CloseButton> : null}</Box>
					<Formik initialValues={formInitialState} onSubmit={handleConfirmSms}>
						{({ values }) => {
							return (
								<Form>
									<DialogTitle classes={{ root: styles.sign__title }}>{t('code_bmg_placeholder')}</DialogTitle>
									<DialogContent sx={{ paddingBottom: '40px' }}>
										<div className={cn('mx-auto', 'mt-4')}>
											<FormikField name={FORM_NAME.confirmCode}>
												{({ field }) => <InputOTP size={6} {...field} />}
											</FormikField>
										</div>
										<div className={styles.buttonSmsWrapper}>
											<Button type="submit" disabled={!(values?.confirmCode?.length === 6)}>
												{t('approve_teenager')}
											</Button>
											<Button type="bare" onClick={handleCloseApproveSms}>
												{t('cancel')}
											</Button>
										</div>
									</DialogContent>
								</Form>
							)
						}}
					</Formik>
				</Dialog>
			)}
		</>
	)
}
